import type { FC, RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { VariableSizeList } from "react-window";

import { NoStoresPopup } from "@locators/components/shared/components/NoStoresPopup/NoStoresPopup";
import { StoreLocatorMobileList } from "@locators/components/shared/components/StoreLocatorMobileList";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { Button } from "@jti/core-components/components/Button";
import { BREAKPOINT_LARGE } from "@jti/fe-common";
import { useDimension } from "@jti/ui/hooks/useDimension";

import { useHeight, useStoreLocatorContext } from "../../hooks";
import { useDisplayType } from "../../hooks/useDisplayType";
import { useLocatorTranslations } from "../../hooks/useLocatorTranslations";
import { useStoreTrackingEvent } from "../../hooks/useStoreTrackingEvent";
import { getStoreDetailsPageUrl } from "../../utils";
import { StoreLocatorCard } from "../StoreLocatorCard";
import { StoreLocatorCardV2 } from "../StoreLocatorCardV2";
import { StoreLocatorCount } from "../StoreLocatorCount";

import { SingleRowForMapView } from "./components/SingleRowForMapView";

import cardStyles from "../StoreLocatorCard/StoreLocatorCard.module.scss";
import styles from "./StoreLocatorCardList.module.scss";

const MOBILE_BREAKDOWN = 768;
const LIST_HEIGHT = 700;
const GUTTER_SIZE_VERTICAL = "var(--des-dimension-gap-and-padding-general-away-default)";
const GUTTER_SIZE_HORIZONTAL = "var(--des-dimension-gap-and-padding-general-near-large)";

type StoreLocatorCardListProps = {
  isMapView: boolean;
  mapWrapperRef?: RefObject<HTMLDivElement>;
};

export const StoreLocatorCardList: FC<StoreLocatorCardListProps> = ({ isMapView, mapWrapperRef }) => {
  const { width } = useDimension();
  const { __ } = useLocatorTranslations();
  const publishStoreLocatorResultEvent = useStoreTrackingEvent();
  const { storeLocatorLayoutVersion = "v1", isSeoFriendlyStoreLocatorEnabled } = useConfig();
  const {
    activeStoreId,
    flotiqStores,
    handleSelectStore,
    onChooseStore,
    isNoMatchingFlotiqStores,
    storeDetailsPage,
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    isMultiBrandSticksEnabled,
    isMapActive,
    isNoStoresSearch,
    isNoStoresFilters,
    isGeneralAnnouncementEnabled,
    isAvailableServicesEnabled,
    isStoreTypeEnabled,
    isVendingMachineEnabled,
    isVendingMachineTipEnabled,
    isNearbyShopsEnabled,
    defaultZoom,
    isAsianAddressFormatEnabled,
    translationPrefix,
  } = useStoreLocatorContext();
  const { isMap } = useDisplayType("map");
  const isMobileView = width < MOBILE_BREAKDOWN;
  const isV2 = storeLocatorLayoutVersion === "v2";
  const isMobileOrTablet = width < BREAKPOINT_LARGE;
  const shouldMeasureListHeight = isV2 && !isMobileOrTablet;
  const { ref: resultsListRef, height } = useHeight<HTMLDivElement>(shouldMeasureListHeight);
  const listRef = useRef<VariableSizeList>(null);
  const rowHeights = useRef<Record<number, number>>({});
  const setRowHeight = (index: number, size: number) => {
    if (rowHeights.current[index] !== size) {
      rowHeights.current[index] = size;
      if (listRef.current) {
        listRef.current.resetAfterIndex(0);
      }
    }
  };
  const showNoStoresPopup = isV2 && flotiqStores?.length === 0 && (isNoStoresSearch?.value || isNoStoresFilters?.value);
  const noStoresPopupType = isNoStoresSearch?.value
    ? isNoStoresSearch.type
    : isNoStoresFilters?.value
      ? isNoStoresFilters.type
      : "search";

  const getItemSize = (index: number) => {
    return rowHeights.current[index] || 300;
  };

  const scrollToActiveElement = useCallback(() => {
    if (resultsListRef.current) {
      const activeIndex = flotiqStores?.findIndex(({ id }) => id === activeStoreId) || 0;
      if (activeIndex >= 0) {
        listRef.current?.scrollToItem(activeIndex);
        const activeElement = resultsListRef.current.querySelector(`.${cardStyles.resultCardActive}`);
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
      }
    }
  }, [activeStoreId, flotiqStores, resultsListRef]);

  const onClickCard = (storeId: string, storeName: string, storetype: string) => {
    publishStoreLocatorResultEvent(storeId, storeName, storetype);
    handleSelectStore(storeId);
    scrollToActiveElement();
  };

  const renderRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const store = flotiqStores?.[index];
    const storeDetailsPageUrl = getStoreDetailsPageUrl({
      storeDetailsPage,
      store,
      defaultZoom,
      isOpeningHoursEnabled,
      isOpeningHoursBreaksEnabled,
      isAvailableDevicesEnabled,
      isAvailableSticksEnabled,
      isMultiBrandSticksEnabled,
      isGeneralAnnouncementEnabled,
      isAvailableServicesEnabled,
      isStoreTypeEnabled,
      isVendingMachineEnabled,
      isVendingMachineTipEnabled,
      isNearbyShopsEnabled,
      isAsianAddressFormatEnabled,
      isSeoFriendlyUrlsEnabled: isSeoFriendlyStoreLocatorEnabled,
    });

    if (!store) {
      return null;
    }

    const resolvedCtaUrl = store?.CtaHref || storeDetailsPageUrl;

    return (
      <div style={style}>
        <div
          style={{ paddingBlockEnd: GUTTER_SIZE_VERTICAL, marginInlineEnd: GUTTER_SIZE_HORIZONTAL }}
          ref={(node) => {
            if (node) {
              const height = node.getBoundingClientRect().height;
              setRowHeight(index, height);
            }
          }}
        >
          {isV2 && isMobileOrTablet ? (
            <StoreLocatorCardV2
              isListView
              store={store}
              mapWrapperRef={mapWrapperRef}
              storeDetailsPageUrl={resolvedCtaUrl}
              ctaLabel={store?.CtaLabel}
              isAddressLabelVisible={translationPrefix !== "horeca"}
              onSelectCard={publishStoreLocatorResultEvent}
            />
          ) : (
            <StoreLocatorCard
              isActive={activeStoreId === store?.id}
              onSelectCard={onClickCard}
              store={store}
              isListView={true}
              storeDetailsPageUrl={resolvedCtaUrl}
              ctaLabel={store?.CtaLabel}
            >
              {onChooseStore && (
                <Button variant="primary" onClick={() => onChooseStore(store)} disabled={isNoMatchingFlotiqStores}>
                  {__("storelocator.select_store")}
                </Button>
              )}
            </StoreLocatorCard>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (listRef.current) {
      rowHeights.current = {};
      listRef.current.resetAfterIndex(0);
    }
  }, [flotiqStores]);

  useEffect(() => {
    if (activeStoreId && (!isMobileView || !isMapView)) {
      scrollToActiveElement();
    }
  }, [activeStoreId, scrollToActiveElement, isMapView, isMobileView]);

  return (
    <div
      ref={resultsListRef}
      className={classNames(styles.listContainer, {
        [styles.v2]: isV2,
      })}
      data-testid="storeLocatorCardList"
    >
      {isMapView && (isMobileView || (isV2 && isMobileOrTablet)) ? (
        <>
          {showNoStoresPopup &&
            mapWrapperRef?.current &&
            ReactDOM.createPortal(
              <NoStoresPopup
                className={classNames(styles.noStoresPopupMap, {
                  [styles.noStoresPopupHidden]: isMapActive && isMap,
                })}
                type={noStoresPopupType}
              />,
              mapWrapperRef.current
            )}
          <SingleRowForMapView
            activeStoreId={activeStoreId ?? ""}
            flotiqStores={flotiqStores}
            mapWrapperRef={mapWrapperRef}
          />
        </>
      ) : (
        <>
          {(!isMobileView || isV2) && <StoreLocatorCount />}
          {showNoStoresPopup && (
            <NoStoresPopup className={classNames(styles.noStoresPopupList)} type={noStoresPopupType} />
          )}
          {isV2 && isMobileOrTablet ? (
            <StoreLocatorMobileList />
          ) : (
            <VariableSizeList
              ref={listRef}
              direction="vertical"
              height={isV2 ? (height ?? LIST_HEIGHT) : LIST_HEIGHT}
              itemSize={getItemSize}
              itemCount={flotiqStores?.length || 0}
              width="100%"
            >
              {renderRow}
            </VariableSizeList>
          )}
        </>
      )}
    </div>
  );
};
